import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Repeat,
  Play,
  Pencil,
  Trash2,
  ArrowLeft,
  Plus,
  CalendarClock,
  Search,
  X,
  RefreshCw,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LayoutList,
  Calendar,
  AlertCircle,
  Download,
  Zap,
} from "lucide-react";
import Loader from "../Loader";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import "./InvoiceStyle.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { useFilter } from "../../FilterContext";
import RecurringInvoicesSummary from "./RecurringInvoicesSummary";
import RecurringFilterDrawer from "./RecurringFilterDrawer";
import RecurringScheduleDetailDrawer from "./RecurringScheduleDetailDrawer";
import RecurringCalendarView from "./RecurringCalendarView";
import {
  fetchRecurringList,
  fetchAllRecurringTemplates,
  bulkRecurringAction,
  processDueRecurring,
  fetchRecurringExportRows,
} from "./recurringInvoiceApi";
import {
  STATUS_TABS,
  matchesCalendarDate,
  needsAttention,
  formatInr,
  formatScheduleDate,
  frequencyLabel,
  computeSummary,
  exportRecurringCsv,
} from "./recurringInvoiceUtils";

const PERIOD_MAP = {
  Today: "Today",
  "This week": "Weekly",
  "This month": "Monthly",
  "All Time": "All Time",
};

const resolvePeriodFilter = (period) => {
  if (typeof period === "string") return PERIOD_MAP[period] || period;
  if (period?.type === "Custom Date") return period;
  return "All Time";
};

const RecurringInvoices = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const { entriesPerPage, sortOrder, setShowDrawer, customDate } = useFilter();

  const [templates, setTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [tabCounts, setTabCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [processingDue, setProcessingDue] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [statusTab, setStatusTab] = useState("all");
  const [summaryPeriod, setSummaryPeriod] = useState("All Time");
  const [customerFilter, setCustomerFilter] = useState("");
  const [frequencyFilter, setFrequencyFilter] = useState("");
  const [sortField, setSortField] = useState("next_run");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [calendarDate, setCalendarDate] = useState(null);
  const [showProjected, setShowProjected] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const skipListEffect = useRef(true);

  const listFilters = useMemo(
    () => ({
      mainId: isLoggedIn?.main_id,
      token,
      statusTab,
      searchQuery,
      customerFilter,
      frequencyFilter,
      customDate,
      summaryPeriod,
      sortField,
      sortOrder,
      page: viewMode === "calendar" ? 1 : currentPage,
      limit: viewMode === "calendar" ? 500 : entriesPerPage,
    }),
    [
      isLoggedIn?.main_id,
      token,
      statusTab,
      searchQuery,
      customerFilter,
      frequencyFilter,
      customDate,
      summaryPeriod,
      sortField,
      sortOrder,
      currentPage,
      entriesPerPage,
      viewMode,
    ]
  );

  const fetchAllTemplates = async () => {
    if (!isLoggedIn?.main_id) return;
    try {
      const data = await fetchAllRecurringTemplates(isLoggedIn.main_id, token);
      setAllTemplates(data);
    } catch {
      /* summary can fall back to list data */
    }
  };

  const fetchList = async (silent = false) => {
    if (!isLoggedIn?.main_id) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await fetchRecurringList(listFilters);
      setTemplates(data.items || []);
      setListTotal(data.total || 0);
      setListTotalPages(data.totalPages || 1);
      setTabCounts(data.counts || {});
    } catch {
      toast.error("Failed to load auto-billing schedules");
      setTemplates([]);
      setListTotal(0);
      setListTotalPages(1);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const refreshAll = async (silent = false) => {
    await Promise.all([fetchAllTemplates(), fetchList(silent)]);
  };

  const refreshTemplateDetail = async (id) => {
    if (!id) return null;
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    if (skipListEffect.current) {
      skipListEffect.current = false;
      return;
    }
    fetchList(true);
  }, [listFilters]);

  useEffect(() => {
    const scheduleId = searchParams.get("schedule");
    const source = allTemplates.length ? allTemplates : templates;
    if (!scheduleId || source.length === 0) return;
    const tpl = source.find((t) => String(t.id) === String(scheduleId));
    if (tpl) {
      setSelectedTemplate(tpl);
      setSearchParams({}, { replace: true });
    }
  }, [allTemplates, templates, searchParams, setSearchParams]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [
    searchQuery,
    statusTab,
    summaryPeriod,
    customerFilter,
    frequencyFilter,
    customDate.from,
    customDate.to,
    sortOrder,
    sortField,
    entriesPerPage,
    calendarDate,
    viewMode,
    showProjected,
  ]);

  const periodFilter = resolvePeriodFilter(summaryPeriod);
  const summarySource = allTemplates.length ? allTemplates : templates;

  const calendarTemplates = useMemo(() => {
    if (!calendarDate) return templates;
    return templates.filter((tpl) => matchesCalendarDate(tpl, calendarDate, showProjected));
  }, [templates, calendarDate, showProjected]);

  const attentionCount = useMemo(
    () => computeSummary(summarySource, periodFilter).attentionCount,
    [summarySource, periodFilter]
  );

  const totalPages = listTotalPages;
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = listTotal ? (currentPage - 1) * entriesPerPage : 0;
  const currentTemplates = viewMode === "table" ? templates : calendarTemplates;

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  const paginate = (page) => setCurrentPage(page);

  const syncAfterAction = async (templateId) => {
    await refreshAll(true);
    if (selectedTemplate && String(selectedTemplate.id) === String(templateId)) {
      const updated = await refreshTemplateDetail(templateId);
      if (updated) setSelectedTemplate(updated);
      else setSelectedTemplate(null);
    }
  };

  const handleCatchUp = async (tpl) => {
    const count = tpl.missed_runs || 0;
    if (count <= 0) return;

    const result = await Swal.fire({
      title: "Catch up missed invoices?",
      text: `Generate up to ${Math.min(count, 12)} overdue invoice(s) for "${tpl.template_name}".`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Catch up",
    });
    if (!result.isConfirmed) return;

    setActionId(tpl.id);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}/catch-up`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || "Catch-up failed");
        return;
      }
      toast.success(data.message || "Catch-up complete");
      await syncAfterAction(tpl.id);
    } catch {
      toast.error("Network error");
    } finally {
      setActionId(null);
    }
  };

  const runAction = async (tpl, path, successMsg) => {
    setActionId(tpl.id);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}/${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.reason === "previous_unpaid") {
          toast.warning(
            "Skipped — the last invoice from this schedule is still unpaid. Collect payment first, or edit the schedule and uncheck “Skip if unpaid”.",
            { autoClose: 9000 }
          );
        } else {
          toast.error(data.message || "Action failed");
        }
        return;
      }
      toast.success(successMsg || data.message);
      if (path === "run-now" && data.invoice?.id) {
        toast.info(`Created ${data.invoice.invoice_number}`, { autoClose: 6000 });
      }
      await syncAfterAction(tpl.id);
    } catch {
      toast.error("Network error");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = (tpl) => {
    Swal.fire({
      title: "Delete billing schedule?",
      text: `"${tpl.template_name}" will be removed. Existing invoices are kept.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setActionId(tpl.id);
      try {
        const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        toast.success("Billing schedule deleted");
        setSelectedTemplate(null);
        setSelectedIds((prev) => prev.filter((id) => id !== tpl.id));
        await refreshAll(true);
      } catch (err) {
        toast.error(err.message || "Delete failed");
      } finally {
        setActionId(null);
      }
    });
  };

  const statusBadge = (tpl) => {
    if (tpl.last_skip_reason === "previous_unpaid") {
      return (
        <span className="recurring-page__status recurring-page__status--blocked">
          Waiting for payment
        </span>
      );
    }
    const s = String(tpl.status || "active").toLowerCase();
    return (
      <span className={`recurring-page__status recurring-page__status--${s}`}>{s}</span>
    );
  };

  const openTemplate = (tpl) => setSelectedTemplate(tpl);

  const renderScheduleActions = (tpl, stopPropagation = true) => (
    <div
      className="quotes-page__actions recurring-page__actions"
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      <button
        type="button"
        className="quotes-page__action-btn"
        disabled={actionId === tpl.id}
        onClick={() => runAction(tpl, "run-now", "Invoice generated")}
      >
        Run now
      </button>
      {tpl.status === "active" ? (
        <button
          type="button"
          className="quotes-page__action-btn"
          disabled={actionId === tpl.id}
          onClick={() => runAction(tpl, "pause", "Schedule paused")}
        >
          Pause
        </button>
      ) : tpl.status === "paused" ? (
        <button
          type="button"
          className="quotes-page__action-btn"
          disabled={actionId === tpl.id}
          onClick={() => runAction(tpl, "resume", "Schedule resumed")}
        >
          Resume
        </button>
      ) : null}
      {tpl.missed_runs > 0 && tpl.status === "active" && (
        <button
          type="button"
          className="quotes-page__action-btn quotes-page__action-btn--primary"
          disabled={actionId === tpl.id}
          onClick={() => handleCatchUp(tpl)}
        >
          Catch up
        </button>
      )}
      <button
        type="button"
        className="quotes-page__action-btn recurring-page__icon-action"
        title="Edit"
        onClick={() => navigate(`/recurring-invoices/edit/${tpl.id}`)}
      >
        <Pencil size={14} />
      </button>
      <button
        type="button"
        className="quotes-page__action-btn recurring-page__icon-action"
        title="Delete"
        disabled={actionId === tpl.id}
        onClick={() => handleDelete(tpl)}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );

  const pageIds = currentTemplates.map((tpl) => tpl.id);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length || bulkLoading) return;

    const labels = {
      pause: "pause",
      resume: "resume",
      delete: "delete",
    };
    const label = labels[action];

    const result = await Swal.fire({
      title: `${label.charAt(0).toUpperCase() + label.slice(1)} selected schedules?`,
      text: `${selectedIds.length} schedule(s) will be ${label === "delete" ? "removed" : label + "d"}.`,
      icon: action === "delete" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonColor: action === "delete" ? "#d33" : undefined,
    });
    if (!result.isConfirmed) return;

    setBulkLoading(true);
    try {
      const data = await bulkRecurringAction(action, selectedIds, isLoggedIn.main_id, token);
      toast.success(data.message || "Bulk action complete");
      setSelectedIds([]);
      setSelectedTemplate(null);
      await refreshAll(true);
    } catch (err) {
      toast.error(err.message || "Bulk action failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleProcessDue = async () => {
    const result = await Swal.fire({
      title: "Process due schedules now?",
      text: "Generates invoices for all active schedules whose next run date is today or earlier.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Run now",
    });
    if (!result.isConfirmed) return;

    setProcessingDue(true);
    try {
      const data = await processDueRecurring(isLoggedIn.main_id, token);
      if (data.skipped && data.reason === "cron_disabled") {
        toast.warning("Recurring cron is disabled on the server.");
        return;
      }
      toast.success(
        `Checked ${data.checked || 0}: ${data.generated || 0} generated, ${data.skipped || 0} skipped`
      );
      await refreshAll(true);
    } catch (err) {
      toast.error(err.message || "Could not process due schedules");
    } finally {
      setProcessingDue(false);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const rows = await fetchRecurringExportRows(listFilters);
      if (!rows.length) {
        toast.info("No schedules to export for the current filters.");
        return;
      }
      exportRecurringCsv(rows);
      toast.success(`Exported ${rows.length} schedule(s)`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const hasAnySchedules = allTemplates.length > 0 || listTotal > 0;

  if (loading) return <Loader />;

  return (
    <div className="product-form-page product-form-page--recurring">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">Auto-billing</h1>
          <p className="product-form-page__subtitle">
            Recurring invoice schedules for your customers. Runs daily at 8:00 AM IST.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/invoice")}
          >
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Invoices
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Search schedules"
            aria-pressed={showSearchBar}
            onClick={() => setShowSearchBar((v) => !v)}
          >
            <Search size={18} />
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Export CSV"
            onClick={handleExportCsv}
            disabled={exporting}
          >
            <Download size={18} />
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Process due schedules"
            onClick={handleProcessDue}
            disabled={processingDue}
            title="Process due schedules now"
          >
            <Zap size={18} />
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Refresh schedules"
            onClick={() => refreshAll(true)}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? "recurring-page__spin" : ""} />
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Open filters"
            onClick={() => setShowDrawer(true)}
          >
            <i className="fa-solid fa-sliders" />
          </button>
          <button
            type="button"
            className="oms-add-btn"
            onClick={() => navigate("/recurring-invoices/new")}
          >
            <Plus size={16} style={{ marginRight: 6 }} />
            New schedule
          </button>
        </div>
      </div>

      {summarySource.length > 0 && (
        <RecurringInvoicesSummary
          templates={summarySource}
          period={summaryPeriod}
          onPeriodChange={setSummaryPeriod}
        />
      )}

      {attentionCount > 0 && statusTab !== "attention" && (
        <div className="recurring-page__attention-banner" role="status">
          <AlertCircle size={18} />
          <p>
            <strong>{attentionCount}</strong> schedule{attentionCount !== 1 ? "s" : ""} need
            attention — missed runs, blocked invoices, or overdue dates.
          </p>
          <button
            type="button"
            className="recurring-page__attention-btn"
            onClick={() => {
              setStatusTab("attention");
              setCurrentPage(1);
            }}
          >
            View all
          </button>
        </div>
      )}

      {showSearchBar && (
        <div className="quotes-page__search">
          <input
            type="search"
            className="quotes-page__search-input"
            placeholder="Search schedule, customer, or invoice #…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="quotes-page__search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearchQuery("");
              setShowSearchBar(false);
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {hasAnySchedules && (
        <div className="recurring-page__controls">
          <div className="recurring-page__tabs" role="tablist" aria-label="Schedule status filters">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={statusTab === tab.id}
                className={`recurring-page__tab ${statusTab === tab.id ? "recurring-page__tab--active" : ""}`}
                onClick={() => {
                  setStatusTab(tab.id);
                  setCurrentPage(1);
                }}
              >
                {tab.label} <span>({tabCounts[tab.id] ?? 0})</span>
              </button>
            ))}
          </div>

          <div className="recurring-page__view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === "table" ? "is-active" : ""}
              onClick={() => {
                setViewMode("table");
                setCalendarDate(null);
              }}
            >
              <LayoutList size={16} />
              Table
            </button>
            <button
              type="button"
              className={viewMode === "calendar" ? "is-active" : ""}
              onClick={() => setViewMode("calendar")}
            >
              <Calendar size={16} />
              Calendar
            </button>
          </div>
        </div>
      )}

      {!hasAnySchedules && !loading ? (
        <div className="recurring-empty" role="status" aria-live="polite">
          <div className="recurring-empty__icon" aria-hidden>
            <Repeat size={40} strokeWidth={1.5} />
          </div>
          <h2 className="recurring-empty__title">No auto-billing schedules yet</h2>
          <p className="recurring-empty__desc">
            Set up a billing schedule once and invoices will be generated automatically on your
            chosen frequency — checked every day at 8:00 AM IST.
          </p>
          <ul className="recurring-empty__features">
            <li>
              <CalendarClock size={16} aria-hidden />
              Weekly, monthly, quarterly, or yearly
            </li>
            <li>
              <Play size={16} aria-hidden />
              Run manually anytime with one click
            </li>
          </ul>
          <button
            type="button"
            className="oms-add-btn recurring-empty__cta"
            onClick={() => navigate("/recurring-invoices/new")}
          >
            <Plus size={18} aria-hidden />
            Set up auto-billing
          </button>
        </div>
      ) : listTotal === 0 && !loading ? (
        <div className="quotes-page__empty" role="status">
          <Repeat size={40} strokeWidth={1.25} />
          <p>No schedules match the current filters, search, or date range.</p>
          <button
            type="button"
            className="quotes-page__action-btn"
            onClick={() => {
              setSearchQuery("");
              setStatusTab("all");
              setSummaryPeriod("All Time");
              setCustomerFilter("");
              setFrequencyFilter("");
              setCalendarDate(null);
            }}
          >
            Clear filters
          </button>
        </div>
      ) : viewMode === "calendar" ? (
        <RecurringCalendarView
          templates={templates}
          selectedDate={calendarDate}
          onSelectDate={setCalendarDate}
          onOpenTemplate={openTemplate}
          includeProjected={showProjected}
          onToggleProjected={setShowProjected}
        />
      ) : (
        <>
          {selectedIds.length > 0 && (
            <div className="recurring-page__bulk-bar">
              <span>{selectedIds.length} selected</span>
              <button
                type="button"
                className="quotes-page__action-btn"
                disabled={bulkLoading}
                onClick={() => runBulkAction("pause")}
              >
                Pause
              </button>
              <button
                type="button"
                className="quotes-page__action-btn"
                disabled={bulkLoading}
                onClick={() => runBulkAction("resume")}
              >
                Resume
              </button>
              <button
                type="button"
                className="quotes-page__action-btn recurring-detail__delete"
                disabled={bulkLoading}
                onClick={() => runBulkAction("delete")}
              >
                Delete
              </button>
              <button
                type="button"
                className="quotes-page__action-btn"
                onClick={() => setSelectedIds([])}
              >
                Clear
              </button>
            </div>
          )}

          <div className="recurring-page__result-meta">
            Showing {listTotal ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, listTotal)} of{" "}
            {listTotal} schedule{listTotal !== 1 ? "s" : ""}
          </div>

          <div className="quotes-page__table-wrap recurring-page__table-wrap recurring-page__desktop-table">
            <table className="quotes-page__table recurring-page__table">
              <colgroup>
                <col className="recurring-col-check" />
                <col className="recurring-col-schedule" />
                <col className="recurring-col-customer" />
                <col className="recurring-col-status" />
                <col className="recurring-col-frequency" />
                <col className="recurring-col-amount" />
                <col className="recurring-col-next" />
                <col className="recurring-col-last" />
                <col className="recurring-col-runs" />
                <col className="recurring-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="recurring-page__check-col">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAllPage}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th>Schedule</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Frequency</th>
                  <th>Amount</th>
                  <th className="recurring-page__cell--nowrap">Next run</th>
                  <th className="recurring-page__cell--nowrap">Last inv.</th>
                  <th className="recurring-page__cell--nowrap">Runs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTemplates.map((tpl) => (
                  <tr
                    key={tpl.id}
                    className={`recurring-page__row ${needsAttention(tpl) ? "recurring-page__row--attention" : ""} ${selectedTemplate?.id === tpl.id ? "recurring-page__row--selected" : ""}`}
                    onClick={() => openTemplate(tpl)}
                  >
                    <td className="recurring-page__check-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(tpl.id)}
                        onChange={() => toggleSelected(tpl.id)}
                        aria-label={`Select ${tpl.template_name}`}
                      />
                    </td>
                    <td>
                      <span className="recurring-page__name-link">{tpl.template_name}</span>
                      {tpl.missed_runs > 0 && tpl.status === "active" && (
                        <span className="recurring-page__missed-pill">
                          {tpl.missed_runs} missed
                        </span>
                      )}
                    </td>
                    <td>{tpl.customer_name || "—"}</td>
                    <td>{statusBadge(tpl)}</td>
                    <td className="recurring-page__cell--nowrap">{frequencyLabel(tpl)}</td>
                    <td className="recurring-page__cell--nowrap">{formatInr(tpl.grand_total)}</td>
                    <td className="recurring-page__cell--nowrap">
                      <span
                        className={
                          tpl.status === "active" &&
                          tpl.next_run_date &&
                          String(tpl.next_run_date) < new Date().toISOString().split("T")[0]
                            ? "recurring-page__overdue-date"
                            : ""
                        }
                      >
                        {formatScheduleDate(tpl.next_run_date)}
                      </span>
                    </td>
                    <td>
                      {tpl.last_invoice ? (
                        <button
                          type="button"
                          className="recurring-page__invoice-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/invoice-detail/${tpl.last_invoice.id}`);
                          }}
                        >
                          {tpl.last_invoice.invoice_number}
                          <ExternalLink size={12} />
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="recurring-page__cell--nowrap">{tpl.runs_count || 0}</td>
                    <td>{renderScheduleActions(tpl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="recurring-page__mobile-list">
            {currentTemplates.map((tpl) => (
              <article
                key={tpl.id}
                className={`recurring-page__card ${needsAttention(tpl) ? "recurring-page__card--attention" : ""}`}
              >
                <div className="recurring-page__card-head">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(tpl.id)}
                    onChange={() => toggleSelected(tpl.id)}
                    aria-label={`Select ${tpl.template_name}`}
                  />
                  <div className="recurring-page__card-title">
                    <button type="button" onClick={() => openTemplate(tpl)}>
                      {tpl.template_name}
                    </button>
                    <p className="recurring-page__card-customer">{tpl.customer_name || "—"}</p>
                  </div>
                  {statusBadge(tpl)}
                </div>
                <dl className="recurring-page__card-grid">
                  <div>
                    <dt>Frequency</dt>
                    <dd>{frequencyLabel(tpl)}</dd>
                  </div>
                  <div>
                    <dt>Amount</dt>
                    <dd>{formatInr(tpl.grand_total)}</dd>
                  </div>
                  <div>
                    <dt>Next run</dt>
                    <dd>{formatScheduleDate(tpl.next_run_date)}</dd>
                  </div>
                  <div>
                    <dt>Runs</dt>
                    <dd>{tpl.runs_count || 0}</dd>
                  </div>
                </dl>
                <div className="recurring-page__card-actions">{renderScheduleActions(tpl, false)}</div>
              </article>
            ))}
          </div>

          {listTotal > entriesPerPage && (
            <div className="recurring-page__pagination">
              <button type="button" onClick={() => paginate(1)} disabled={currentPage === 1} aria-label="First page">
                <ChevronsLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <ChevronLeft size={18} />
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <ChevronRight size={18} />
              </button>
              <button
                type="button"
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                aria-label="Last page"
              >
                <ChevronsRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      <RecurringFilterDrawer
        templates={allTemplates.length ? allTemplates : templates}
        customerFilter={customerFilter}
        setCustomerFilter={setCustomerFilter}
        frequencyFilter={frequencyFilter}
        setFrequencyFilter={setFrequencyFilter}
        sortField={sortField}
        setSortField={setSortField}
      />

      <RecurringScheduleDetailDrawer
        template={selectedTemplate}
        open={Boolean(selectedTemplate)}
        onClose={() => setSelectedTemplate(null)}
        actionId={actionId}
        onRunNow={(tpl) => runAction(tpl, "run-now", "Invoice generated")}
        onPause={(tpl) => runAction(tpl, "pause", "Schedule paused")}
        onResume={(tpl) => runAction(tpl, "resume", "Schedule resumed")}
        onCatchUp={handleCatchUp}
        onDelete={handleDelete}
      />

      <style>{`
        .product-form-page--recurring .recurring-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          margin: clamp(1.5rem, 5vw, 2.5rem) auto 0;
          padding: clamp(2.5rem, 6vw, 3.5rem) clamp(1.25rem, 4vw, 2rem);
          max-width: 28rem;
          border-radius: calc(var(--sp-radius) + 4px);
          border: 1px dashed rgba(255, 255, 255, 0.14);
          background: linear-gradient(165deg, rgba(14, 20, 56, 0.95) 0%, rgba(22, 29, 71, 0.85) 100%);
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3);
        }
        .product-form-page--recurring .recurring-empty__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 4.5rem;
          height: 4.5rem;
          margin-bottom: 1.25rem;
          border-radius: calc(var(--sp-radius) + 2px);
          background: rgba(99, 102, 241, 0.12);
          color: #818cf8;
          box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.25);
        }
        .product-form-page--recurring .recurring-empty__title {
          margin: 0;
          font-size: clamp(1.05rem, 1rem + 0.3vw, 1.25rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--sp-text);
        }
        .product-form-page--recurring .recurring-empty__desc {
          margin: 0.65rem 0 0;
          font-size: var(--sp-fs-sm);
          color: var(--sp-muted);
          line-height: 1.55;
          max-width: 22rem;
        }
        .product-form-page--recurring .recurring-empty__features {
          list-style: none;
          margin: 1.35rem 0 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          width: 100%;
          max-width: 20rem;
        }
        .product-form-page--recurring .recurring-empty__features li {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.8125rem;
          font-weight: 550;
          color: rgba(238, 241, 255, 0.72);
          text-align: left;
        }
        .product-form-page--recurring .recurring-empty__features li svg {
          flex-shrink: 0;
          color: #818cf8;
        }
        .product-form-page--recurring .recurring-empty__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          margin-top: 1.65rem;
          min-height: var(--sp-touch);
          padding: 0.6rem 1.5rem;
          font-size: var(--sp-fs-sm);
          font-weight: 650;
          border-radius: var(--sp-radius);
          background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
          border: none;
          box-shadow: 0 6px 24px rgba(99, 102, 241, 0.35);
        }
        .product-form-page--recurring .product-form-page__header-actions .oms-add-btn {
          display: inline-flex;
          align-items: center;
        }
        .recurring-page__spin {
          animation: recurring-spin 0.8s linear infinite;
        }
        @keyframes recurring-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RecurringInvoices;
